import { useCallback, useState } from 'react';
import { validateField, VALIDATION_RULES } from '../../../utils/validation';
import logger from '../../../utils/logger';

/**
 * Per-field validation state for PostForm.
 *
 * Two concerns:
 *   - `validationErrors` / `touchedFields` drive inline error rendering
 *     under each input (`touched && errors` pattern).
 *   - `validateFieldAndUpdateErrors` runs `validateField` against the
 *     song rules and merges the result into `validationErrors` (removing
 *     the key when clean so the UI can treat absence as "no error").
 *
 * `handleFieldBlur` is the bound-to-input handler: it flips the touched
 * flag and validates the sanitized current value for that specific
 * field. The caller supplies `getSanitizedValues` so we can stay
 * agnostic about where the raw values live (several different hooks
 * own different slices of PostForm state).
 */
const usePostFormValidation = ({ getSanitizedValues }) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const validateFieldAndUpdateErrors = useCallback((fieldName, value) => {
    const rules = VALIDATION_RULES.song[fieldName];
    if (!rules) return true;

    const errors = validateField(value, rules);
    const debugValue =
      typeof value === 'string'
        ? value
        : Array.isArray(value)
          ? JSON.stringify(value)
          : value;
    logger.debug('PostForm validation', {
      fieldName,
      value: debugValue,
      length: typeof value === 'string' ? value.length : undefined,
      errors,
    });
    setValidationErrors((prev) => {
      const next = { ...prev };
      if (errors.length > 0) {
        next[fieldName] = errors;
      } else {
        delete next[fieldName];
      }
      return next;
    });
    return errors.length === 0;
  }, []);

  const handleFieldBlur = useCallback(
    (fieldName) => {
      setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
      const sanitizedValues = getSanitizedValues();
      validateFieldAndUpdateErrors(fieldName, sanitizedValues[fieldName]);
    },
    [getSanitizedValues, validateFieldAndUpdateErrors]
  );

  /**
   * Validate the full form against `VALIDATION_RULES.song`, skipping
   * `description` (enforced by the editor's CharacterCount extension).
   * Returns `{ ok, errors }`; when !ok, also stamps every field as
   * touched so the inline errors surface at once.
   */
  const validateAll = useCallback(
    (formData) => {
      const errors = {};
      let hasErrors = false;
      for (const [fieldName, rules] of Object.entries(VALIDATION_RULES.song)) {
        if (fieldName === 'description') continue;
        const fieldErrors = validateField(formData[fieldName], rules);
        if (fieldErrors.length > 0) {
          errors[fieldName] = fieldErrors;
          hasErrors = true;
        }
      }

      if (hasErrors) {
        setValidationErrors(errors);
        setTouchedFields(
          Object.keys(formData).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          )
        );
      }
      return { ok: !hasErrors, errors };
    },
    []
  );

  const clearUrlError = useCallback(() => {
    setValidationErrors((prev) => ({ ...prev, URL: [] }));
  }, []);

  return {
    validationErrors,
    touchedFields,
    handleFieldBlur,
    validateAll,
    clearUrlError,
  };
};

export default usePostFormValidation;
